import React, { FC, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";
import { useParams } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useIntl } from "react-intl";

import { updateBackgroundLoadingState } from "store/store";
import { ShipmentTrackingContext } from "pages/common/ShipmentTracking/ShipmentTrackingContext";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import usePermissions from "hooks/usePermissions";
import testIds from "constants/testIds";
import PickupDeliveryProofDialog from "./PickupDeliveryProofDialog/PickupDeliveryProofDialog";

type PickupDeliveryProofProps = {
  setIsShipmentDelivered: React.Dispatch<React.SetStateAction<boolean>>;
  isOffPlatform: boolean;
};

const PickupDeliveryProof: FC<PickupDeliveryProofProps> = function (props) {
  const { setIsShipmentDelivered, isOffPlatform } = props;

  const intl = useIntl();
  const queryClient = useQueryClient();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const { id } = useParams();
  const { showSubmitPodDialog, setShowSubmitPodDialog } = useContext(ShipmentTrackingContext);
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");

  const { data: shipmentDetails = {} } = useGetShipmentDetails(id!, true, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { data: trackingReferencesData } = useGetTrackingReferences(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const isPoPSubmitted = shipmentDetails?.schedule?.is_proof_of_pickup_submitted;
  const canSubmitWithoutTracking = trackingReferencesData?.tracking_settings?.can_submit_pod_without_tracking;

  const refetchQueries = () => {
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentGeoDataDetails(id!) });
    queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.shipmentInvoicesListing(id!) });
  };

  const handleOpenDialog = () => {
    if (canSubmitWithoutTracking) {
      setShowSubmitPodDialog(true);
    }
  };

  const submitPODButtonLabel =
    user_type === "shipper" || isPoPSubmitted || isOffPlatform
      ? intl.formatMessage({
          id: "components.molecules.top-nav.submitPOD",
          defaultMessage: "Submit proof of delivery",
        })
      : intl.formatMessage({
          id: "components.molecules.top-nav.submitPOPorPOD",
          defaultMessage: "Submit proof of pickup / delivery",
        });

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 0 }}
        overlay={
          !canSubmitWithoutTracking ? (
            <Tooltip id="pod-tooltip">
              {intl.formatMessage({
                id: "tracking.components.podDialog.cannotSubmit",
                defaultMessage: "Cannot submit POP or POD without tracking number",
              })}
            </Tooltip>
          ) : (
            <span />
          )
        }
      >
        <button
          type="button"
          className="cta-button outline"
          onClick={handleOpenDialog}
          disabled={!canSubmitWithoutTracking}
          data-testid={testIds.SHIPMENT_DASHBOARD_SUBMIT_POD_BUTTON}
        >
          {submitPODButtonLabel}
        </button>
      </OverlayTrigger>
      <PickupDeliveryProofDialog
        show={showSubmitPodDialog}
        onHide={() => setShowSubmitPodDialog(false)}
        isPoPSubmitted={isPoPSubmitted}
        setIsShipmentDelivered={setIsShipmentDelivered}
        refetchFn={refetchQueries}
        isOffPlatform={isOffPlatform}
      />
    </>
  );
};

export default PickupDeliveryProof;
